// Human validation messages — ONE Zod v4 error map for every form and every
// validator (2026-09-18, audit UX-05).
//
// WHY HERE. Zod's default text is written for a developer reading a stack
// trace: "Too small: expected string to have >=1 characters", "Invalid input:
// expected number, received string". The audit found exactly those strings
// under "Full name" and "Identifier" on the Add patron form, and the same
// schemas validate on the worker, so the wording is a cross-module contract —
// DEV-GUIDE-001 §3 puts it in packages/, and @erp/core-schemas is the one
// package both lanes already import (browser-safe, zod-only). A module never
// re-words a default message locally; it passes LABELS.
//
// THREE LAYERS, one vocabulary:
//   humanMessage(issue, label)  the pure function: a structured issue + the
//                               field's label → "Full name is required".
//   humanErrorMap               Zod's `customError` hook. It knows only the
//                               path, so the label is the key humanised
//                               ("displayName" → "Display name"). A schema's
//                               own `error:` still wins — Zod resolves
//                               inst-level messages before this map.
//   fieldErrors(issues, labels) what a form calls after `safeParse`: first
//                               issue per top-level key, re-worded with the
//                               form's REAL labels ("Full name"), custom
//                               schema messages passed through untouched.
//
// Install once per composition root: `installHumanErrorMap()` in a portal's
// main.tsx (and a worker's entry, when its 422s should read the same way).
// `fieldErrors` does not depend on the install — it recognises Zod's default
// text and replaces it — so a form is human even where nobody installed it.
import { z } from "zod";

/** The structured part of a Zod v4 issue this module reads — a `$ZodIssue` or a raw issue inside the error map. */
export type IssueLike = {
  readonly code?: string;
  readonly path?: readonly PropertyKey[];
  readonly message?: string;
  readonly input?: unknown;
  readonly origin?: string;
  readonly minimum?: number | bigint;
  readonly maximum?: number | bigint;
  readonly inclusive?: boolean;
  readonly exact?: boolean;
  readonly expected?: string;
  readonly format?: string;
  readonly values?: readonly unknown[];
  readonly divisor?: number;
};

/** Tokens a key may contain that read as initialisms, not words. */
const INITIALISMS: Record<string, string> = {
  id: "ID",
  url: "URL",
  html: "HTML",
  isbn: "ISBN",
  issn: "ISSN",
  marc: "MARC",
  pdf: "PDF",
  csv: "CSV",
  otp: "OTP",
  upi: "UPI",
  gst: "GST",
  pan: "PAN",
};

/**
 * A field label from its key: camelCase, snake_case and kebab-case split into
 * words, sentence-cased, initialisms restored — `displayName` → "Display
 * name", `parentEmail` → "Parent email", `groupId` → "Group ID". Array
 * indices are skipped (`tags.0` → "Tags"); an empty path is "This value".
 */
export function fieldLabel(path: readonly PropertyKey[] | PropertyKey | undefined): string {
  const keys = Array.isArray(path) ? path : path === undefined ? [] : [path];
  const key = [...keys].reverse().find((k) => typeof k === "string" && !/^\d+$/.test(k));
  if (typeof key !== "string" || !key) return "This value";
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => INITIALISMS[w.toLowerCase()] ?? w.toLowerCase());
  const [first, ...rest] = words;
  const head = first ? (INITIALISMS[first.toLowerCase()] ?? first.charAt(0).toUpperCase() + first.slice(1)) : "";
  return [head, ...rest].join(" ");
}

const n = (v: number | bigint | undefined): string => (v === undefined ? "" : String(v));
const plural = (count: number | bigint, one: string, many: string): string => `${n(count)} ${count === 1 || count === 1n ? one : many}`;
const lower = (label: string): string => (label === label.toUpperCase() ? label : label.charAt(0).toLowerCase() + label.slice(1));

/** How the format families read in a sentence. */
const FORMAT_NOUN: Record<string, string> = {
  email: "email address",
  url: "web address (URL)",
  uuid: "ID",
  guid: "ID",
  ulid: "ID",
  date: "date (YYYY-MM-DD)",
  datetime: "date and time",
  time: "time (HH:MM)",
  duration: "duration",
  ipv4: "IP address",
  ipv6: "IP address",
  e164: "phone number with a country code",
  base64: "value",
  jwt: "token",
  emoji: "emoji",
};

/**
 * Was the value absent, as opposed to the wrong kind? A raw issue (inside the
 * error map) carries `input`; a final issue does not, so the English default
 * ("…received undefined") or an already-humanised line is read instead.
 */
const isMissing = (issue: IssueLike): boolean =>
  "input" in issue
    ? issue.input === undefined || issue.input === null || issue.input === ""
    : /received (undefined|null)\b/.test(issue.message ?? "") || / is required$/.test(issue.message ?? "");

/**
 * The message a person should read for one issue, given the field's label.
 * Returns undefined for the codes that carry no field meaning (a custom
 * refinement's own text, unrecognised keys), so the caller keeps Zod's message.
 */
export function humanMessage(issue: IssueLike, label: string): string | undefined {
  switch (issue.code) {
    case "invalid_type": {
      if (isMissing(issue)) return `${label} is required`;
      switch (issue.expected) {
        case "number":
        case "int":
        case "bigint":
          return `${label} must be a number`;
        case "boolean":
          return `${label} must be yes or no`;
        case "date":
          return `${label} must be a valid date`;
        case "array":
          return `${label} must be a list`;
        case "string":
          return `${label} must be text`;
        default:
          return `${label} is not valid`;
      }
    }
    case "too_small": {
      const min = issue.minimum ?? 0;
      const one = min === 1 || min === 1n;
      switch (issue.origin) {
        case "string":
          if (one) return `${label} is required`;
          if (issue.exact) return `${label} must be exactly ${plural(min, "character", "characters")}`;
          return `${label} must be at least ${plural(min, "character", "characters")}`;
        case "array":
        case "set":
          if (one) return `Add at least one to ${lower(label)}`;
          return `${label} needs at least ${plural(min, "entry", "entries")}`;
        case "file":
          return `${label} is too small`;
        case "date":
          return `${label} is too early`;
        default:
          return issue.inclusive === false ? `${label} must be more than ${n(min)}` : `${label} must be at least ${n(min)}`;
      }
    }
    case "too_big": {
      const max = issue.maximum ?? 0;
      switch (issue.origin) {
        case "string":
          if (issue.exact) return `${label} must be exactly ${plural(max, "character", "characters")}`;
          return `${label} must be at most ${plural(max, "character", "characters")}`;
        case "array":
        case "set":
          return `${label} can have at most ${plural(max, "entry", "entries")}`;
        case "file":
          return `${label} is too large`;
        case "date":
          return `${label} is too late`;
        default:
          return issue.inclusive === false ? `${label} must be less than ${n(max)}` : `${label} must be at most ${n(max)}`;
      }
    }
    case "invalid_format": {
      const format = issue.format ?? "";
      if (format === "email") {
        return /email/i.test(label) ? `Enter a valid email address` : `${label} must be a valid email address`;
      }
      if (format === "url") return /url|link|address|website/i.test(label) ? `Enter a valid web address` : `${label} must be a valid web address`;
      if (format === "regex" || format === "includes" || format === "starts_with" || format === "ends_with") {
        return `${label} is not in the expected format`;
      }
      const noun = FORMAT_NOUN[format];
      return noun ? `${label} must be a valid ${noun}` : `${label} is not valid`;
    }
    case "invalid_value": {
      const values = (issue.values ?? []).filter((v) => typeof v === "string" || typeof v === "number") as (string | number)[];
      const options = values.length > 0 && values.length <= 6 ? ` (${values.join(", ")})` : "";
      return `Choose a valid ${lower(label)}${options}`;
    }
    case "not_multiple_of":
      return `${label} must be a multiple of ${n(issue.divisor)}`;
    case "invalid_union":
      return `${label} is not valid`;
    // A refinement's own text is the message; unrecognised keys and container
    // issues are not field errors a person can fix in a form.
    case "custom":
    case "unrecognized_keys":
    case "invalid_key":
    case "invalid_element":
    default:
      return undefined;
  }
}

/** Zod v4 `customError` hook — the path's last key, humanised, is the label. */
export const humanErrorMap: z.core.$ZodErrorMap = (issue) => humanMessage(issue as IssueLike, fieldLabel(issue.path));

/**
 * Install the map on the process's Zod instance (idempotent). Call it once
 * in each composition root — a portal's main.tsx, a worker's entry — before
 * the first parse. A schema's own `error:` text still wins.
 */
export function installHumanErrorMap(): void {
  if (z.config().customError === humanErrorMap) return;
  z.config({ customError: humanErrorMap });
}

const enLocale = z.locales.en().localeError;

/**
 * Is this the text Zod itself would have produced — its English default, or
 * this module's key-derived line? Anything else is a message the schema author
 * wrote (`z.string().min(3, "Pick a longer slug")`), which a form must keep.
 * `invalid_type` is compared up to the "received …" tail, which the final
 * issue no longer carries the input for.
 */
export function isDefaultMessage(issue: IssueLike): boolean {
  const message = issue.message ?? "";
  if (!message) return true;
  if (message === humanMessage(issue, fieldLabel(issue.path))) return true;
  const en = enLocale?.(issue as never);
  const enText = typeof en === "string" ? en : en?.message;
  if (!enText) return false;
  if (issue.code === "invalid_type") return message.split(", received")[0] === enText.split(", received")[0];
  return message === enText;
}

/**
 * The record a form keeps from a failed `safeParse`: the FIRST issue per
 * top-level key, worded for a person with the form's own labels —
 * `fieldErrors(error.issues, { displayName: "Full name" })` →
 * `{ displayName: "Full name is required" }`. A key without a label falls
 * back to the humanised key; a schema author's custom message is kept.
 */
export function fieldErrors(
  issues: readonly IssueLike[],
  labels: Readonly<Record<string, string>> = {},
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    const key = String(issue.path?.[0] ?? "");
    if (!key || out[key]) continue;
    const label = labels[key] ?? fieldLabel(issue.path);
    const human = isDefaultMessage(issue) ? humanMessage(issue, label) : undefined;
    out[key] = human ?? issue.message ?? `${label} is not valid`;
  }
  return out;
}
